"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
Object.defineProperty(exports, "__esModule", { value: true });
var d3 = require("d3");
var _ = require("lodash");
var event = require("./event");
var log_1 = require("./log");
var template_1 = require("./template");
var common_tags_1 = require("common-tags");
var global_1 = require("./global");
var logger = log_1.getLogger("visualizer");
var Visualizer = /** @class */ (function () {
    function Visualizer(template, selector, options) {
        this.outlets = {};
        this.locals = {};
        this._initCanvas = false;
        this._initialized = false;
        this._isInDataUpdateSession = false;
        this._disableDataUpdate = false;
        // init
        this.isRedraw = false;
        this.size = { width: 0, height: 0 };
        this.setUpBridgeLater();
        // option: data
        this.data = undefined;
        this._storage = {};
        this._listeners = {};
        if (typeof options.data === "object") {
            this.initData(options.data);
        }
        this.transformers = options.transformers || {};
        this.eventHandlers = options.eventHandlers || {};
        this._selector = selector;
        this.exElementList = options.elements;
        this._beforeDrawHook = options.beforeDraw;
        this._afterDrawHook = options.afterDraw;
        // parse option
        global_1.default.currentVisualizer = this;
        var est = template_1.loadTemplate(template, options.useBVT ? "bvt" : "xml");
        if (!est) {
            throw new Error("Error parsing template. Aborted.");
        }
        this.rootComponent = template_1.instantiate(est);
        this.rootComponent.$visualizer = this;
        global_1.default.currentVisualizer = null;
    }
    Visualizer.prototype.initCanvas = function () {
        var canvas = d3.select(this._selector);
        if (canvas.empty()) {
            logger.error("Cannot get the element");
        }
        this.svg = canvas.select("svg");
        this.svg.attr("width", "100%");
        this.defs = this.svg.append("defs");
        this.rootComponent.dom = this.svg.append("g");
        // tslint:disable-next-line
        this.rootComponent["_appended"] = true;
    };
    Visualizer.prototype.initData = function (dataSet) {
        var _this = this;
        _.assign(this._storage, dataSet);
        if (typeof this.data === "undefined") {
            this.data = new Proxy(this._storage, {
                get: function (target, prop) {
                    return target[prop];
                },
                set: function (obj, prop, value) {
                    obj[prop] = value;
                    if (_this._isInDataUpdateSession || _this._disableDataUpdate) {
                        return true;
                    }
                    var listeners = _this._listeners[prop.toString()];
                    if (listeners) {
                        listeners.forEach(function (x) {
                            x.updateData();
                            x.render();
                        });
                    }
                    return true;
                },
            });
        }
    };
    // MARK: data update
    Visualizer.prototype.triggerDataChange = function (keys) {
        var allListeners = [];
        for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
            var key = keys_1[_i];
            // for each key
            var listeners = this._listeners[key];
            var value = this._storage[key];
            // update listeners
            if (listeners) {
                listeners.forEach(function (x) {
                    // update data
                    x.updateData();
                    // record listener
                    if (allListeners.indexOf(x) < 0) {
                        allListeners.push(x);
                    }
                });
            }
        }
        // render all listeners
        allListeners.forEach(function (x) { return x.render(); });
    };
    Visualizer.prototype.dataUpdateSession = function (session) {
        this._isInDataUpdateSession = true;
        var keys = session.call(this, this.data);
        this._isInDataUpdateSession = false;
        if (keys) {
            this.triggerDataChange(keys);
        }
    };
    Visualizer.prototype.run = function () {
        this.startDraw();
    };
    Visualizer.prototype.startDraw = function () {
        global_1.default.currentVisualizer = this;
        window.$v = this;
        this.willDraw();
        this.draw();
        this.didDrawn();
        event.emit("bvd3.drawFinished");
        global_1.default.currentVisualizer = null;
    };
    Visualizer.prototype.willDraw = function () {
        // reset all components
        if (this._initialized) {
            this.rootComponent.dom.html("");
            var resetComponent_1 = function (comp) {
                comp.reset();
                var elements = comp.elements;
                if (typeof elements !== "undefined") {
                    for (var _i = 0, elements_1 = elements; _i < elements_1.length; _i++) {
                        var el = elements_1[_i];
                        resetComponent_1(el);
                    }
                }
            };
            resetComponent_1(this.rootComponent);
        }
        // hook
        if (this._beforeDrawHook) {
            this._beforeDrawHook.call(this, this._initialized);
        }
        event.emit(event.BEFORE_DRAW);
    };
    Visualizer.prototype.didDrawn = function () {
        if (this._afterDrawHook) {
            this._afterDrawHook.call(this, this._initialized);
        }
        this._initialized = true;
        event.emit(event.AFTER_DRAW);
    };
    Visualizer.prototype.clearAll = function () { return; };
    Visualizer.prototype.draw = function () {
        this.rootComponent.render();
        if (this.size.height <= 0) {
            var bbox = this.rootComponent.dom.node().getBBox();
            this.size.height = -bbox.y + bbox.height + 100;
        }
        this.updateSVGSize();
    };
    Visualizer.prototype.redraw = function () {
        this.rootComponent.remove();
        this.draw();
    };
    Visualizer.prototype.updateSVGSize = function () {
        var _this = this;
        var getSize = function (size) {
            return typeof size === "function" ? size.call(_this) : size;
        };
        this.svg
            .attr("width", getSize(this.size.width))
            .attr("height", getSize(this.size.height));
    };
    Visualizer.prototype.forceReRender = function () {
        var _this = this;
        var ids = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            ids[_i] = arguments[_i];
        }
        ids.forEach(function (id) {
            _this.outlets[id].remove();
            _this.outlets[id].render();
        });
    };
    Visualizer.prototype.setComponent = function (component) {
        this.rootComponent = component;
    };
    Visualizer.prototype.appendDef = function (tag, id) {
        this.defs.selectAll("#" + id).remove();
        return this.defs.append(tag).attr("id", id);
    };
    Visualizer.prototype.appendGradient = function (id) {
        var def;
        if (arguments.length === 2) {
            def = arguments[1];
        }
        else {
            def = { x1: 0, x2: 0, y1: 0, y2: 0, stops: [] };
            var direction = arguments[1];
            var stops = arguments[2];
            if (direction === "horizontal") {
                def.x2 = 100;
            }
            else {
                def.y2 = 100;
            }
            def.stops = stops.map(function (s, i) { return ({ offset: i * 100, color: s, opacity: 1 }); });
        }
        this.appendDef("linearGradient", id)
            .attr("x1", def.x1 + "%").attr("x2", def.x2 + "%").attr("y1", def.y1 + "%").attr("y2", def.y2 + "%")
            .html(def.stops.map(function (s) { return common_tags_1.oneLineTrim(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n            <stop offset=\"", "%\" style=\"\n                stop-color:", ";\n                stop-opacity:", "\" />\n            "], ["\n            <stop offset=\"", "%\" style=\"\n                stop-color:", ";\n                stop-opacity:", "\" />\n            "])), s.offset, s.color, s.opacity === undefined ? 1 : s.opacity); }).join());
    };
    Visualizer.prototype.registerListener = function (key, element) {
        if (typeof this._listeners[key] === "undefined") {
            this._listeners[key] = [];
        }
        this._listeners[key].push(element);
    };
    Visualizer.prototype.setUpBridgeLater = function () {
        var _this = this;
        document.addEventListener("DOMContentLoaded", function () {
            var bridge = window.gon || {};
            _this.bridge = {
                proj: bridge.project,
                sample: bridge.curr_sample,
                vrnt: bridge.curr_variant,
            };
        });
    };
    Visualizer.prototype.__pos__ = function (el) {
        this.__activeComponent__ = el;
        // tslint:disable-next-line
        console.log(this.rootComponent.__tree__);
        this.__activeComponent__ = undefined;
    };
    Visualizer.prototype.getElementWithUUID = function (uuid) {
        var result;
        this.rootComponent.applyAll(function (el) {
            if (el._uuid === uuid) {
                result = el;
                return true;
            }
        });
        return result;
    };
    return Visualizer;
}());
exports.Visualizer = Visualizer;
var templateObject_1;
//# sourceMappingURL=visualizer.js.map


//////////////////
// WEBPACK FOOTER
// ./node_modules/bvd3/dist/visualizer.js
// module id = 884
// module chunks = 0 1